import React, { useContext, useMemo } from 'react'
import { Redirect, Route, Switch } from 'react-router-dom'

import AppContext from 'contexts/AppContext'
import useDefaultTab from 'hooks/useDefaultTab'
import useFeature, { featureTypes } from 'hooks/useFeature'
import { accountType } from 'models/Account'

import AccountVerificationPage from 'pages/registration/AccountVerificationPage'
import ChooseOrganizationPage from 'pages/auth/ChooseOrganizationPage'
import CompletedPage from 'pages/signup/CompletedPage'
import CompleteRegistrationPage from 'pages/registration/CompleteRegistrationPage'
import ESignSuccessPage from 'pages/signup/ESignSuccessPage'
import EulaView from 'pages/webviews/Eula'
import ThirdPartyServicesConsentView from 'pages/webviews/ThirdPartyServicesConsent'
import EulaPage from 'pages/registration/EulaPage'
import ForgotPasswordPage from 'pages/auth/ForgotPasswordPage'
import InviteProvidersPage from 'pages/signup/InviteProvidersPage'
import LoginPage from 'pages/auth/LoginPage'
import NotFoundPage from 'pages/common/NotFoundPage'
import OrganizationLoginPage from 'pages/auth/OrganizationLoginPage'
import ResetPasswordPage from 'pages/auth/ResetPasswordPage'
import SetupAccountPage from 'pages/signup/SetupAccountPage'
import SetupOrganizationPage from 'pages/signup/SetupOrganizationPage'
import SetupPasswordPage from 'pages/signup/SetupPasswordPage'
import SetupUsernamePage from 'pages/signup/SetupUsernamePage'
import SignupPage from 'pages/signup/SignupPage'
import SNSiteDownPage from 'pages/signup/SNSiteDownPage'
import OauthRedirect from 'pages/auth/OauthRedirect'
import VerifySessionPage from 'pages/auth/VerifySessionPage'

import InternalRoute from 'components/routes/InternalRoute'
import ExternalLoginRoute from 'components/routes/ExternalLoginRoute'
import LoginRoute from 'components/routes/LoginRoute'
import SignupRoute from 'components/routes/SignupRoute'
import isVetBuddyProduct, { isSurgicalNotesProduct } from 'lib/CheckProduct'
import MfaSetupPage from 'pages/auth/MfaSetupPage'
import MfaAuthenticatorSetupPage from 'pages/auth/MfaAuthenticatorSetupPage'
import MfaVerifyPage from 'pages/auth/MfaVerifyPage'
import MfaBackupSelectionPage from 'pages/auth/MfaBackupSelectionPage'
import MfaBackupSmsPage from 'pages/auth/MfaBackupSmsPage'
import MfaSetupSuccessPage from 'pages/auth/MfaSetupSuccessPage'

function useInternalRoutes() {
  const { currentAccount } = useContext(AppContext)

  const canManageNotifications = useFeature(featureTypes.MANAGE_NOTIFICATIONS)
  const canViewAdmin = currentAccount && currentAccount.administrator
  const canManageIndependentEncounters = useFeature(
    featureTypes.MANAGE_INDEPENDENT_ENCOUNTERS
  )
  const canViewIntegrationHub =
    useFeature(featureTypes.VIEW_INTEGRATION_HUB) &&
    currentAccount?.type === accountType.STAFF
  const canViewBeta = useFeature(featureTypes.VIEW_BETA)
  const canViewReporting =
    useFeature(featureTypes.VIEW_REPORTS) && !isVetBuddyProduct()
  const hasOpNote = useFeature(featureTypes.ENABLE_APPOINTMENT_OPERATIVE_REPORT)
  const canViewDashboard =
    useFeature(featureTypes.VIEW_DASHBOARD) &&
    (currentAccount?.type === accountType.STAFF ||
      (currentAccount?.type === accountType.PROVIDER && hasOpNote))

  let internalRoutes = useMemo(() => {
    /*
        NOTE: Order is important. The details pages should be before the list pages.
        /patient/:id should be before /patients
      */

    let routes = [
      {
        path: '/notes',
        component: React.lazy(() => import('pages/internal/NotesPage')),
        title: 'Notes'
      },
      {
        exact: true,
        path: '/schedule/enterprise-forms/new',
        component: React.lazy(() => import('pages/internal/CreateAIFormPage')),
        title: 'Create AI Form'
      },
      {
        path: '/schedule/enterprise-forms/:id',
        component: React.lazy(() => import('pages/internal/AIFormDetailsPage')),
        title: 'Enterprise AI Form'
      },
      {
        path: '/appointments/:id',
        component: React.lazy(() => import('pages/internal/AppointmentPage')),
        title: 'Appointment Details'
      },
      {
        path: '/patients/:id',
        component: React.lazy(() => import('pages/internal/PatientPage')),
        title: 'Patient Details'
      },
      {
        path: '/help/article/:id',
        component: React.lazy(() => import('pages/internal/HelpArticlePage')),
        title: 'Help'
      },
      {
        path: '/help/release/:id',
        component: React.lazy(() => import('pages/internal/ReleaseNotePage')),
        title: 'Help'
      },
      {
        path: '/admin/licensing/users/:id',
        component: React.lazy(() => import('pages/internal/UserPage')),
        title: 'User'
      },
      {
        path: '/help',
        component: React.lazy(() => import('pages/internal/HelpPage')),
        title: 'Help'
      },
      {
        path: '/patients',
        component: React.lazy(() => import('pages/internal/PatientsPage')),
        title: 'Patients'
      },
      {
        path: '/profile',
        component: React.lazy(() => import('pages/internal/ProfilePage')),
        title: 'Profile'
      },
      {
        path: '/schedule',
        component: React.lazy(() => import('pages/internal/SchedulePage')),
        title: 'Schedule'
      },
      {
        path: '/block-times/:id',
        component: React.lazy(() =>
          import('pages/internal/AvailabilityDetailsPage')
        ),
        title: 'Block Times Details'
      }
    ].filter(Boolean)

    if (canViewAdmin) {
      routes = [
        ...routes,
        {
          path: '/admin',
          component: React.lazy(() => import('pages/internal/AdminPage')),
          title: 'Admin'
        }
      ]
    }

    if (canManageIndependentEncounters) {
      routes = [
        ...routes,
        {
          path: '/encounters/:id',
          component: React.lazy(() => import('pages/internal/EncounterPage')),
          title: 'Encounter Details'
        }
      ]
    }

    if (canManageNotifications) {
      routes = [
        ...routes,
        {
          path: '/inbox/notifications',
          component: React.lazy(() => import('pages/internal/InboxPage')),
          title: 'Inbox Notification'
        }
      ]
    }

    routes = [
      ...routes,
      {
        path: '/encounters',
        component: React.lazy(() => import('pages/internal/EncountersPage')),
        title: 'Encounters'
      },
      {
        path: '/on-call',
        component: React.lazy(() => import('pages/internal/OnCallPage')),
        title: 'On Call Records'
      },
      {
        path: '/voice-commands',
        component: React.lazy(() => import('pages/internal/VoiceCommandsPage')),
        title: 'Voice Commands'
      }
    ]

    if (canViewIntegrationHub) {
      routes = [
        ...routes,
        {
          path: '/integration-hub/inbox',
          component: React.lazy(() =>
            import('pages/internal/IntegrationHubPage')
          ),
          title: 'Inbox - Integration Hub'
        },
        {
          path: '/integration-hub/requests',
          component: React.lazy(() =>
            import('pages/internal/IntegrationHubPage')
          ),
          title: 'Requests - Integration Hub'
        },
        {
          path: '/integration-hub/patients',
          component: React.lazy(() =>
            import('pages/internal/IntegrationHubPage')
          ),
          title: 'Patients - Integration Hub'
        },
        {
          path: '/integration-hub/activity',
          component: React.lazy(() =>
            import('pages/internal/IntegrationHubPage')
          ),
          title: 'Activity - Integration Hub'
        },
        {
          path: '/integration-hub',
          component: React.lazy(() =>
            import('pages/internal/IntegrationHubPage')
          ),
          title: 'Integration Hub'
        }
      ]
    }

    if (canViewBeta) {
      routes = [
        ...routes,
        {
          path: '/beta',
          component: React.lazy(() => import('pages/internal/BetaPage')),
          title: 'DocBuddy - Beta',
          layoutProps: { hideSideBar: true }
        }
      ]
    }

    if (canViewReporting) {
      routes = [
        ...routes,
        {
          path: '/reporting',
          component: React.lazy(() => import('pages/internal/ReportingPage')),
          title: 'Reporting'
        }
      ]
    }

    if (canViewDashboard) {
      routes = [
        ...routes,
        {
          path: '/dashboard',
          component: React.lazy(() => import('pages/internal/DashboardPage')),
          title: 'Dashboard'
        }
      ]
    }

    return routes
  }, [
    canManageNotifications,
    canViewAdmin,
    currentAccount,
    canManageIndependentEncounters,
    canViewIntegrationHub,
    canViewBeta,
    canViewReporting,
    canViewDashboard
  ])

  if (isSurgicalNotesProduct()) {
    internalRoutes = []
  }

  return internalRoutes
}

function AppRouter() {
  const { isLoggedIn } = useContext(AppContext)
  const defaultTab = useDefaultTab()
  const internalRoutes = useInternalRoutes()

  if (!internalRoutes.length) {
    return (
      <Switch>
        <Route
          path="/"
          render={() => (
            <SignupRoute
              component={SNSiteDownPage}
              title="Surgical Notes"
              layoutProps={{ hideNavbar: true }}
            />
          )}
        />
      </Switch>
    )
  }

  return (
    <Switch>
      <Route
        exact
        path="/"
        render={() => (
          <Redirect replace to={isLoggedIn ? defaultTab : '/login'} />
        )}
      />
      <Route
        path="/webviews/third-party-services-consent/:lang"
        render={() => <ThirdPartyServicesConsentView />}
      />
      <Route path="/webviews/eula/:lang" render={() => <EulaView />} />
      <SignupRoute
        path="/completed"
        component={CompletedPage}
        title="Setup Password"
      />
      <SignupRoute
        path="/esign-success"
        component={ESignSuccessPage}
        title="e-Sign Success"
      />
      <SignupRoute path="/signup" component={SignupPage} title="Sign up" />
      <SignupRoute
        path="/setup/:token/account"
        component={SetupAccountPage}
        title="Set up Account"
      />
      <SignupRoute
        path="/setup/:token/organization"
        component={SetupOrganizationPage}
        title="Set up Organization"
      />
      <SignupRoute
        path="/setup/:token/invite-providers"
        component={InviteProvidersPage}
        title="Invite Providers"
      />
      <SignupRoute
        path="/setup/:token/setup-username"
        component={SetupUsernamePage}
        title="Setup Username"
      />
      <SignupRoute
        path="/setup/:token/password"
        component={SetupPasswordPage}
        title="Setup Password"
      />
      <SignupRoute
        path="/verification"
        component={AccountVerificationPage}
        title="Account Verification"
      />
      <SignupRoute
        path="/complete-registration"
        component={CompleteRegistrationPage}
        title="Complete Registration"
      />
      <SignupRoute
        path="/shared/:token/calendar"
        component={React.lazy(() =>
          import('pages/common/ViewOnCallAvailabilities')
        )}
        title="On Call Schedule"
        layoutProps={{ fullWidthContent: true, isLoggedIn }}
      />

      <ExternalLoginRoute
        path="/verify/saml"
        render={props => <VerifySessionPage {...props} />}
      />
      <ExternalLoginRoute
        path="/oauth-redirect"
        render={props => <OauthRedirect {...props} />}
      />

      <LoginRoute path="/login" component={LoginPage} title="Login" />
      <LoginRoute
        path="/choose-organization"
        component={ChooseOrganizationPage}
        title="Choose Organization"
      />
      <LoginRoute
        path="/organization-login"
        component={OrganizationLoginPage}
        title="Organization Login"
      />
      <LoginRoute
        path="/accept-eula"
        component={EulaPage}
        title="Accept EULA"
      />
      <LoginRoute
        path="/forgot-password"
        component={ForgotPasswordPage}
        title="Forgot Password"
      />
      <LoginRoute
        path="/:type/reset-password/:token"
        component={ResetPasswordPage}
        title="Reset Password"
      />
      <LoginRoute
        path="/mfa/setup"
        component={MfaSetupPage}
        title="Setup MFA"
      />
      <LoginRoute
        path="/mfa/authenticator"
        component={MfaAuthenticatorSetupPage}
        title="Authenticator App"
      />
      <LoginRoute
        path="/mfa/verify"
        component={MfaVerifyPage}
        title="Authenticator App"
      />
      <LoginRoute
        exact
        path="/mfa/backup"
        component={MfaBackupSelectionPage}
        title="MFA enabled"
      />
      <LoginRoute
        path="/mfa/backup/sms"
        component={MfaBackupSmsPage}
        title="MFA SMS Backup"
      />
      <LoginRoute
        path="/mfa/setup-success"
        component={MfaSetupSuccessPage}
        title="MFA Setup Success"
      />
      {internalRoutes.map(routeProps => (
        <InternalRoute key={routeProps.path} {...routeProps} />
      ))}

      <Route component={NotFoundPage} />
    </Switch>
  )
}

export default AppRouter
